import { add, formatDistanceToNow, subHours, subMinutes } from 'date-fns';
import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import {
    autoinject,
    bind,
    componentAutoReaction,
    IDisposable,
    inject,
    Loader,
    PopoutHandle,
    SnapshotPopout,
    TasksStore,
} from 'Mri.Web.Common';
import * as React from 'react';
import { BsArrowsAngleExpand } from '@react-icons/all-files/bs/BsArrowsAngleExpand';
import notFoundImage from '../../../Mri.Web.Common/Assets/image-not-found.png';
import { AppStore } from '../Core/AppStore';
import { ActiveDisplay } from './ActiveDisplay';
import { DisplaysStore } from './DisplaysStore';

export interface IDisplayScreenSnapshotProps {
    readonly screenSnapshot: Mri.Web.Portal.Controllers.Displays.ScreenSnapshotModel;
    readonly className?: string;
    readonly loading?: boolean;
}

@autoinject
@observer
export class DisplayScreenSnapshot extends React.Component<IDisplayScreenSnapshotProps> {
    @bind
    @action
    public async componentDidMount() {
        componentAutoReaction(this, () => this.props.screenSnapshot.uri, this.onChange);

        await this.onLoad();
    }

    public render() {
        const { screenSnapshot } = this.props;

        return (
            <>
                <div
                    className={this.props.className + ' h-100'}
                    onMouseEnter={action(() => (this.showOverlay = true))}
                    onMouseLeave={action(() => (this.showOverlay = false))}
                >
                    <div className="d-flex flex-items-center justify-content-center h-100">
                        <div
                            className={this.landscape ? 'p-relative w-content' : 'p-relative w-content h-100'}
                            style={{ minHeight: '130px' }}
                        >
                            <img
                                alt="Image not found. Try a Refresh"
                                onError={this.onError}
                                onLoad={this.onScreenSnapshotLoad}
                                src={!this.notFound ? screenSnapshot.uri : notFoundImage}
                                style={{
                                    minWidth: '175px',
                                    maxWidth: this.notFound ? '175px' : 'unset',
                                    minHeight: '130px',
                                }}
                                className="screen-snapshot rounded img-fluid"
                            />

                            {this.loading && (
                                <div className="snapshot-refreshing rounded">
                                    <Loader className="text-white text-center p-2" loading={true} showSpinner />
                                </div>
                            )}

                            <div className={this.snapshotOverlayClass}>
                                <div className="row h-100 mx-0">
                                    <div className="col-12 justify-content-between d-flex align-self-start pe-0">
                                        {screenSnapshot.captureType == Mri.ScreenSnapshots.CaptureType.TouchHeatmap && (
                                            <div className="popvu d-inline-block">InfiniteTouch®</div>
                                        )}

                                        {screenSnapshot.captureType == Mri.ScreenSnapshots.CaptureType.Snapshot && (
                                            <div className="popvu d-inline-block">PopVu™</div>
                                        )}

                                        <button className="btn btn-link ms-auto py-1" onClick={this.openPopout}>
                                            <BsArrowsAngleExpand className="text-white" />
                                        </button>
                                    </div>

                                    <div className="col-12 flex-items-center justify-content-center">
                                        <div className="w-100 text-center mt-5">
                                            <button
                                                className="btn btn-outline-white btn-xsm refresh-button"
                                                onClick={this.refreshSnapshot}
                                            >
                                                <span>Refresh</span>
                                            </button>
                                        </div>
                                    </div>

                                    <div className="col-12 align-self-end f-12 text-center">{this.updatedVerbage}</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <SnapshotPopout
                        snapshotUri={screenSnapshot.uri}
                        popoutHandle={this.popoutHandle}
                        landscape={this.landscape}
                        popVu={screenSnapshot.captureType == Mri.ScreenSnapshots.CaptureType.Snapshot}
                        size="full"
                    />
                </div>
            </>
        );
    }

    @bind
    @action
    private async onLoad() {
        if (this.outOfDate) {
            await this.refreshSnapshot();
        }
    }

    @bind
    @action
    private openPopout() {
        this.popoutHandle.open();
    }

    @bind
    @action
    private async refreshSnapshot() {
        this.refreshing = true;
        try {
            await this.displaysStore.loadCurrentScreenSnapshotAsync(this.props.screenSnapshot.displaySerialNumber);
        } catch (e: any) {
            console.error('Error refreshing PopVu™', e);
        } finally {
            this.refreshing = false;
        }
    }

    @bind
    @action
    private async onScreenSnapshotLoad(event: React.SyntheticEvent<HTMLImageElement>) {
        const element = event.target as HTMLImageElement;

        if (!element.width || !element.height) return;

        const aspectRatio = element.width / element.height;

        // Calculate aspect ratio and store it in HTML data- attribute
        element.setAttribute('aspect-ratio', aspectRatio.toString());

        // Conditional statement
        if (this.notFound) {
            // Image not found
            //element.setAttribute('style', 'width: 175px; height: auto; ');
            this.landscape = true;
        } else if (aspectRatio >= 1) {
            // Image is landscape
            this.landscape = true;
            const minHeight = 160;
            const minWidth = 160 * aspectRatio;
            element.setAttribute(
                'style',
                `width: 100%; height: auto; min-height: ${minHeight}px; min-width: ${minWidth}px;`
            );
            this.landscape = true;
        } else if (aspectRatio < 1) {
            // Image is portrait
            this.landscape = false;
            element.setAttribute('style', 'width: auto; height: 100%; min-width: 160px');
        }
    }

    @bind
    @action
    private onError(event: React.SyntheticEvent<HTMLImageElement>) {
        this.notFound = true;
    }

    @bind
    @action
    private onChange() {
        this.notFound = false;
    }

    @computed
    private get snapshotOverlayClass() {
        if (this.showOverlay || this.outOfDate) {
            return 'snapshot-overlay rounded';
        }

        if (!this.showOverlay) {
            return 'd-none';
        }
    }

    @computed
    private get outOfDate() {
        return this.props.screenSnapshot.pollTime < subMinutes(new Date(), 15);
    }

    @computed
    private get loading() {
        return this.refreshing ?? this.props.loading;
    }

    @computed
    private get updatedVerbage() {
        const now = this.appStore.clock.time;
        const snapshot = this.props.screenSnapshot;

        if (snapshot.pollTime < subMinutes(new Date(), 15)) return 'Out of Date';
        if (snapshot.pollTime > now) return 'Updated: A few seconds ago';
        if (snapshot.pollTime < now) {
            return (
                'Updated: ' +
                formatDistanceToNow(snapshot.pollTime, {
                    addSuffix: true,
                })
            );
        }
    }

    @observable
    private landscape = false;
    @observable
    private showOverlay = false;
    @observable
    private refreshing = false;
    @observable
    private notFound = false;
    private popoutHandle = new PopoutHandle();
    @inject
    private appStore!: AppStore;
    @inject
    private displaysStore!: DisplaysStore;
    @inject
    private tasksStore!: TasksStore;
}